import { useState } from 'react';
import { Icon, type IconName } from './icons';
import { cx } from './ui';
import { tr, type MessageKey } from '../i18n';
import {
  applyStoredThemeFull,
  applyThemeToRoot,
  persistTheme,
  type Accent,
  type Appearance,
  type ThemeState,
} from '../lib/theme';

function readBrowserTheme(): ThemeState {
  if (typeof document === 'undefined' || typeof localStorage === 'undefined') {
    return { appearance: 'system', accent: 'blue', contrast: 'standard' };
  }
  return applyStoredThemeFull(localStorage, document.documentElement);
}

/** Update the live theme without reload: only root attributes change, so the bill, the payment draft, the scan queue and pending server attempts are untouched. */
function commit(next: ThemeState, setState: (state: ThemeState) => void): void {
  setState(next);
  try {
    persistTheme(localStorage, next);
  } catch {
    // persistTheme already swallows storage denials; belt and braces
  }
  try {
    applyThemeToRoot(document.documentElement, next);
  } catch {
    // applying must never break the till mid-bill
  }
}

const NEXT_APPEARANCE: Record<Appearance, Appearance> = { system: 'light', light: 'dark', dark: 'system' };
const APPEARANCE_ICON: Record<Appearance, IconName> = { system: 'monitor', light: 'sun', dark: 'moon' };
const APPEARANCE_LABEL: Record<Appearance, MessageKey> = { system: 'theme.auto', light: 'theme.light', dark: 'theme.dark' };

/**
 * Appearance cycler with the same behaviour as ThemeSwitch (legacy sp.theme
 * aware) plus accent/contrast state. Prefer this where accent and contrast
 * are also offered; ThemeSwitch in ui.tsx is left untouched.
 */
export function AppearanceSwitch({ className }: { className?: string }) {
  const [state, setState] = useState<ThemeState>(readBrowserTheme);
  return (
    <button
      type="button"
      className={className}
      aria-label={tr('theme.choice', { choice: tr(APPEARANCE_LABEL[state.appearance]) })}
      title={tr('theme.title', { choice: tr(APPEARANCE_LABEL[state.appearance]) })}
      onClick={() => commit({ ...state, appearance: NEXT_APPEARANCE[state.appearance] }, setState)}
    >
      <Icon name={APPEARANCE_ICON[state.appearance]} size={16} />
      <span>{tr(APPEARANCE_LABEL[state.appearance])}</span>
    </button>
  );
}

const ACCENTS: { value: Accent; label: MessageKey; dot: string }[] = [
  { value: 'blue', label: 'theme.blue', dot: '#0a5fae' },
  { value: 'green', label: 'theme.green', dot: '#15803d' },
  { value: 'violet', label: 'theme.violet', dot: '#6d28d9' },
];

/**
 * Brand accent picker. Every option carries its name as text plus a check mark
 * on the selected one, so meaning never depends on colour alone; labels wrap
 * instead of clipping long Tamil strings.
 */
export function AccentPicker({ className }: { className?: string }) {
  const [state, setState] = useState<ThemeState>(readBrowserTheme);
  return (
    <div
      role="radiogroup"
      aria-label={tr('theme.brand')}
      className={cx('flex flex-wrap items-center gap-2', className)}
    >
      {ACCENTS.map((option) => {
        const selected = state.accent === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={tr(option.label)}
            title={tr(option.label)}
            onClick={() => commit({ ...state, accent: option.value }, setState)}
            className={cx(
              'inline-flex min-h-11 items-center gap-2 rounded-lg border px-3 text-sm font-medium break-words',
              selected ? 'border-[var(--ui-ring)] bg-[var(--ui-primary-soft)]' : 'border-[var(--ui-border)]',
            )}
          >
            <span
              aria-hidden
              className="inline-block h-4 w-4 shrink-0 rounded-full"
              style={{ background: option.dot }}
            />
            <span>{tr(option.label)}</span>
            {selected ? <Icon name="check" size={14} /> : null}
          </button>
        );
      })}
    </div>
  );
}

/** Accessible contrast preset toggle. Standard keeps the current look; high strengthens text, borders and the focus ring. Danger keeps its own colour in both. */
export function ContrastToggle({ className }: { className?: string }) {
  const [state, setState] = useState<ThemeState>(readBrowserTheme);
  const high = state.contrast === 'high';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={high}
      onClick={() => commit({ ...state, contrast: high ? 'standard' : 'high' }, setState)}
      className={cx(
        'inline-flex min-h-11 items-center gap-2 rounded-lg border px-3 text-sm font-medium break-words',
        high ? 'border-[var(--ui-ring)] bg-[var(--ui-primary-soft)]' : 'border-[var(--ui-border)]',
        className,
      )}
    >
      <span>{tr('theme.highContrast')}</span>
      <span aria-hidden>{high ? tr('common.on') : tr('common.off')}</span>
    </button>
  );
}

export function ThemeSettings({ className }: { className?: string }) {
  return (
    <section aria-label={tr('theme.label')} className={cx('flex flex-col gap-3', className)}>
      <div className='flex flex-wrap items-center gap-2'>
        <AppearanceSwitch className='inline-flex min-h-11 items-center gap-2 rounded-lg border border-[var(--ui-border)] px-3 text-sm font-medium break-words' />
        <ContrastToggle />
      </div>
      <AccentPicker />
    </section>
  );
}

export function applyStoredDisplayTheme() {
  if (typeof document === 'undefined' || typeof localStorage === 'undefined') {
    return { appearance: 'system', accent: 'blue', contrast: 'standard' } as const;
  }
  return applyStoredThemeFull(localStorage, document.documentElement);
}
